/**
 * Common style utilities to reduce code duplication
 * These utilities provide consistent styling patterns across the app
 */

// Focus ring styles for form elements
export const focusRingClasses =
  'focus:outline-hidden focus:ring-2 focus:ring-orange-500 focus:border-transparent';

// Hover background styles
export const hoverClasses = {
  light: 'hover:bg-gray-50 dark:hover:bg-gray-700',
  medium: 'hover:bg-gray-50 dark:hover:bg-gray-800',
  dark: 'hover:bg-gray-100 dark:hover:bg-gray-900',
  orange: 'hover:bg-orange-50 dark:hover:bg-orange-900/20',
  red: 'hover:bg-red-50 dark:hover:bg-red-900/20',
  transparent: 'hover:bg-transparent',
} as const;

// Text color styles
export const textColorClasses = {
  primary: 'text-gray-900 dark:text-white',
  secondary: 'text-gray-900 dark:text-gray-100',
  muted: 'text-gray-600 dark:text-gray-400',
  mutedLight: 'text-gray-500 dark:text-gray-500',
  mutedDark: 'text-gray-700 dark:text-gray-300',
  error: 'text-red-600 dark:text-red-400',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  info: 'text-blue-600 dark:text-blue-400',
  orange: 'text-orange-600 dark:text-orange-400',
} as const;

// Background color styles
export const bgColorClasses = {
  primary: 'bg-white dark:bg-gray-800',
  secondary: 'bg-white dark:bg-gray-900',
  elevated: 'bg-gray-50 dark:bg-gray-800',
  muted: 'bg-gray-100 dark:bg-gray-800',
  input: 'bg-white dark:bg-gray-700',
  hover: 'bg-gray-50 dark:bg-gray-700',
} as const;

// Border styles
export const borderClasses = {
  default: 'border-gray-200 dark:border-gray-700',
  light: 'border-gray-200 dark:border-gray-600',
  dark: 'border-gray-300 dark:border-gray-700',
  input: 'border-gray-300 dark:border-gray-600',
  orange: 'border-orange-200 dark:border-orange-800',
} as const;

// Shadow styles
export const shadowClasses = {
  sm: 'shadow-xs',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  hover: 'hover:shadow-md',
  hoverLg: 'hover:shadow-lg',
  dark: 'dark:shadow-gray-900/50',
} as const;

// Transition styles
export const transitionClasses = {
  default: 'transition-all duration-200',
  fast: 'transition-all duration-150',
  slow: 'transition-all duration-300',
  colors: 'transition-colors duration-200',
  shadow: 'transition-shadow duration-200',
  transform: 'transition-transform duration-200',
} as const;

// Disabled styles
export const disabledClasses =
  'disabled:opacity-50 disabled:cursor-not-allowed';

// Common input styles (for form fields)
export const inputBaseClasses = `
  w-full px-3 py-2 rounded-lg
  ${bgColorClasses.input}
  ${borderClasses.input}
  ${textColorClasses.primary}
  placeholder-gray-400 dark:placeholder-gray-500
  ${focusRingClasses}
  ${disabledClasses}
  ${transitionClasses.default}
`.trim();

// Common card styles
export const cardBaseClasses = `
  ${bgColorClasses.primary}
  rounded-lg
  ${borderClasses.default}
  ${shadowClasses.sm}
  ${transitionClasses.shadow}
`.trim();

// Helper function to combine classes conditionally
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Helper function to get text color based on theme
export function getTextColorClass(
  variant: keyof typeof textColorClasses = 'primary'
): string {
  return textColorClasses[variant];
}

// Helper function to get background color based on theme
export function getBgColorClass(
  variant: keyof typeof bgColorClasses = 'primary'
): string {
  return bgColorClasses[variant];
}

// Helper function to get hover classes
export function getHoverClass(
  variant: keyof typeof hoverClasses = 'light'
): string {
  return hoverClasses[variant];
}

// Helper function to get border classes
export function getBorderClass(
  variant: keyof typeof borderClasses = 'default'
): string {
  return borderClasses[variant];
}

// Responsive text size utilities
export const textSizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  responsive: {
    sm: 'text-sm sm:text-base',
    md: 'text-base sm:text-lg',
    lg: 'text-lg sm:text-xl',
    xl: 'text-xl sm:text-2xl',
    '2xl': 'text-2xl sm:text-3xl',
    '3xl': 'text-3xl sm:text-4xl',
  },
} as const;

// Spacing utilities
export const spacingClasses = {
  padding: {
    sm: 'p-2',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8',
    responsive: {
      sm: 'p-2 sm:p-3',
      md: 'p-3 sm:p-4',
      lg: 'p-4 sm:p-6',
      xl: 'p-6 sm:p-8',
    },
  },
  margin: {
    sm: 'm-2',
    md: 'm-4',
    lg: 'm-6',
    xl: 'm-8',
    responsive: {
      sm: 'm-2 sm:m-3',
      md: 'm-3 sm:m-4',
      lg: 'm-4 sm:m-6',
      xl: 'm-6 sm:m-8',
    },
  },
  gap: {
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8',
    responsive: {
      sm: 'gap-2 sm:gap-3',
      md: 'gap-3 sm:gap-4',
      lg: 'gap-4 sm:gap-6',
      xl: 'gap-6 sm:gap-8',
    },
  },
} as const;
